/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { MouseEvent, useMemo } from "react";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinearGradient } from "@visx/gradient";
import { GridRows } from "@visx/grid";
import { Group } from "@visx/group";
import { scaleLinear, scaleBand } from "@visx/scale";
import { BarRounded } from "@visx/shape";
import { Tooltip, useTooltip } from "@visx/tooltip";
import dayjs from "dayjs";
import { When } from "react-if";
import { FlexContainer, Spacer, Text, theme } from "@nordcloud/gnui";
import { ColorBox } from "~/components";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { formatMoney, formatMoneyLabel } from "~/tools";
import { getMinMax } from "../../Charts/utils";
import { useBudgetChart } from "../hooks";

type Props = {
  width: number;
  height: number;
  currentYearData: string[];
  previousYearData: number[];
  showPreviousYear: boolean;
  forecast: number[];
  year: string;
};

const verticalMargin = 40;

export function BudgetChart({
  width,
  height,
  currentYearData,
  previousYearData,
  showPreviousYear,
  forecast,
  year,
}: Props) {
  const xMax = width - 70;
  const yMax = height - verticalMargin;

  const current = dayjs();
  const startOfYear = current.startOf("year");

  const { currency } = useCurrency();

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        range: [1, xMax],
        round: true,
        domain: currentYearData?.map((_, index) => index),
        padding: 0.1,
      }),
    [xMax, currentYearData]
  );

  const yScale = useMemo(() => {
    const [min, max] = getMinMax([
      ...currentYearData.map(Number),
      ...(showPreviousYear ? previousYearData : []),
      ...(showPreviousYear ? forecast : []),
    ]);
    const minimum = 0.1 * (min > 0 ? min : max);
    return scaleLinear<number>({
      range: [yMax, 0],
      round: true,
      domain: [0, max + minimum],
    });
  }, [yMax, currentYearData, previousYearData, forecast, showPreviousYear]);

  const showTooltipElement = (event: MouseEvent, index: number) => {
    const TOOLTIP_MARGIN = -20;

    const [budget, prev, forecasted, month] = [
      currentYearData[index],
      previousYearData[index],
      forecast[index],
      startOfYear.add(index, "month").format(dateFormat.monthName),
    ];

    showTooltip({
      tooltipLeft: event.clientX,
      tooltipTop: event.clientY + TOOLTIP_MARGIN,
      tooltipData: (
        <div key={`${month}-${index}`}>
          <BudgetChartTooltip
            showPreviousYear={showPreviousYear}
            budget={budget}
            prevYearSpend={prev}
            forecast={forecasted}
            month={month}
            year={year}
            forecastBefore={isBefore(index)}
          />
        </div>
      ),
    });
  };

  const {
    getForecast,
    isBefore,
    hasAnyBefore,
    hasAnyAfter,
    OPACITY: { from: fromOpacity, to: toOpacity },
  } = useBudgetChart({
    current,
    year,
    forecast,
    spends: previousYearData,
  });

  return (
    <>
      <svg width={width} height={height} data-testid="budget-chart">
        <LinearGradient
          to={theme.color.support.grey}
          from={theme.color.support.grey}
          id="budgetFill"
        />
        <LinearGradient
          to={theme.color.support.yellow}
          from={theme.color.support.yellow}
          id="previousYearFill"
        />
        <LinearGradient
          to={theme.color.support.yellow}
          from={theme.color.support.yellow}
          fromOpacity={fromOpacity}
          toOpacity={toOpacity}
          id="forecastFill"
        />
        <LinearGradient
          to={theme.color.support.orange}
          from={theme.color.support.orange}
          fromOpacity={fromOpacity}
          toOpacity={toOpacity}
          id="estimationFill"
        />
        <GridRows
          scale={yScale}
          width={xMax}
          top={verticalMargin / 2}
          left={60}
          strokeDasharray="1,3"
          stroke={theme.color.border.focus}
          strokeOpacity={1}
          pointerEvents="none"
          numTicks={5}
        />
        <Group top={verticalMargin / 2} left={60}>
          <AxisLeft
            top={0}
            scale={yScale}
            stroke={theme.color.border.focus}
            tickStroke="transparent"
            numTicks={5}
            tickFormat={(n) => formatMoneyLabel(n as number, currency)}
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: 10,
              textAnchor: "end",
            })}
          />
          <AxisBottom
            top={yMax}
            scale={xScale}
            stroke={theme.color.border.focus}
            tickStroke={theme.color.border.focus}
            numTicks={currentYearData.length}
            tickFormat={(n: number) =>
              startOfYear.add(n, "month").format(dateFormat.monthShort)
            }
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: 8,
              textAnchor: "middle",
            })}
          />
          {currentYearData.map((month, index) => (
            <Bars
              key={`${month}-${index}`}
              showPreviousYear={showPreviousYear}
              xCoefficient={5}
              index={index}
              showTooltipElement={showTooltipElement}
              hideTooltip={hideTooltip}
              xScaleValue={xScale(index)}
              xScaleBandwidth={xScale.bandwidth()}
              yMax={yMax}
              fill="budgetFill"
              yScaleValue={yScale(parseFloat(month))}
            />
          ))}
          {showPreviousYear &&
            previousYearData.map((month, index) => (
              <Bars
                key={`${month}-${index}`}
                showPreviousYear={showPreviousYear}
                xCoefficient={1}
                index={index}
                showTooltipElement={showTooltipElement}
                hideTooltip={hideTooltip}
                xScaleValue={xScale(index)}
                xScaleBandwidth={xScale.bandwidth()}
                yScaleValue={yScale(month)}
                yMax={yMax}
                fill="previousYearFill"
              />
            ))}
          {showPreviousYear &&
            forecast.map((month, index) => (
              <Bars
                key={`${month}-${index}`}
                showPreviousYear={showPreviousYear}
                xCoefficient={1}
                index={index}
                showTooltipElement={showTooltipElement}
                hideTooltip={hideTooltip}
                xScaleValue={xScale(index)}
                xScaleBandwidth={xScale.bandwidth()}
                yScaleValue={yScale(getForecast(index))}
                yMax={yMax}
                fill={isBefore(index) ? "estimationFill" : "forecastFill"}
              />
            ))}
        </Group>
      </svg>
      {tooltipOpen && (
        <Tooltip
          key={Date.now()}
          top={tooltipTop}
          left={tooltipLeft}
          offsetLeft={-100}
          style={{
            backgroundColor: theme.color.background.ui05,
            color: theme.color.text.text04,
            position: "fixed",
            padding: `${theme.spacing.spacing01} ${theme.spacing.spacing02}`,
            fontSize: theme.fontSizes.sm,
            borderRadius: theme.radius.sm,
            transform: "translateY(-100%)",
          }}
        >
          <>{tooltipData}</>
        </Tooltip>
      )}
      <Spacer height={theme.spacing.spacing01} />
      <BudgetChartLegend
        showPreviousYear={showPreviousYear}
        hasAnyBefore={hasAnyBefore}
        hasAnyAfter={hasAnyAfter}
      />
    </>
  );
}

type BarsProps = {
  showPreviousYear?: boolean;
  xCoefficient: number;
  index: number;
  showTooltipElement: (event: MouseEvent, index: number) => void;
  hideTooltip: () => void;
  xScaleValue: number | undefined;
  yScaleValue: number | undefined;
  xScaleBandwidth: number;
  yMax: number;
  fill: string;
};

function Bars({
  showPreviousYear,
  index,
  showTooltipElement,
  hideTooltip,
  xCoefficient,
  xScaleValue,
  xScaleBandwidth,
  yScaleValue,
  yMax,
  fill,
}: BarsProps) {
  const widthCoefficient = showPreviousYear ? 3 : 2;
  const barWidth = xScaleBandwidth / widthCoefficient;
  const barHeight = yMax - (yScaleValue ?? 0);
  const xCoefficientValue = showPreviousYear ? xCoefficient : 1;
  const barX =
    (xScaleValue ?? 0) + (xCoefficientValue * barWidth) / widthCoefficient;
  const barY = yMax - barHeight - 1; // -1 is for the Axis

  return (
    <BarRounded
      radius={16}
      x={barX}
      y={barY}
      width={barWidth}
      height={barHeight}
      fill={`url(#${fill})`}
      onMouseEnter={(event) => showTooltipElement(event, index)}
      onMouseLeave={hideTooltip}
      onMouseMove={(event) => showTooltipElement(event, index)}
    />
  );
}

type BudgetChartLegendProps = {
  showPreviousYear: boolean;
  hasAnyBefore: boolean;
  hasAnyAfter: boolean;
};

function BudgetChartLegend({
  showPreviousYear,
  hasAnyAfter,
  hasAnyBefore,
}: BudgetChartLegendProps) {
  return (
    <FlexContainer direction="row" alignItems="flex-start">
      <FlexContainer
        alignItems="center"
        columnGap={theme.spacing.spacing04}
        marginRight={theme.spacing.spacing04}
      >
        <When condition={showPreviousYear}>
          <FlexContainer columnGap={theme.spacing.spacing01}>
            <ColorBox color={theme.color.support.yellow} />
            <Text size="sm" tag="span" color={theme.color.text.text02}>
              Previous Year Spend
            </Text>
          </FlexContainer>
          <When condition={hasAnyAfter}>
            <FlexContainer columnGap={theme.spacing.spacing01}>
              <ColorBox color={theme.color.support.yellow} opacity={0.33} />
              <Text size="sm" tag="span" color={theme.color.text.text02}>
                Previous Year Forecast
              </Text>
            </FlexContainer>
          </When>
          <When condition={hasAnyBefore}>
            <FlexContainer columnGap={theme.spacing.spacing01}>
              <ColorBox color={theme.color.support.orange} opacity={0.33} />
              <Text size="sm" tag="span" color={theme.color.text.text02}>
                Estimated Previous Year Spend
              </Text>
            </FlexContainer>
          </When>
        </When>
        <FlexContainer columnGap={theme.spacing.spacing01}>
          <ColorBox color={theme.color.support.grey} />
          <Text size="sm" tag="span" color={theme.color.text.text02}>
            Budget
          </Text>
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );
}

function abbreviateMonth(month: string) {
  return dayjs(month, dateFormat.monthName).format(dateFormat.monthShort);
}

type BudgetChartTooltipProps = {
  showPreviousYear: boolean;
  month: string;
  year: string;
  budget: string;
  forecast: number;
  prevYearSpend: number;
  forecastBefore: boolean;
};

function BudgetChartTooltip({
  showPreviousYear,
  month,
  year,
  budget,
  forecast,
  prevYearSpend,
  forecastBefore,
}: BudgetChartTooltipProps) {
  const { currency } = useCurrency();
  const previousYear = useMemo(() => {
    return dayjs(year, dateFormat.year)
      .subtract(1, "year")
      .format(dateFormat.year);
  }, [year]);

  const budgetText = showPreviousYear
    ? `${abbreviateMonth(month)} ${year} `
    : "";

  const prevYearBudgetText = `${abbreviateMonth(month)} ${previousYear} `;

  const budgetValue = parseFloat(budget);

  const showGrowthRate =
    prevYearSpend != null && !isNaN(prevYearSpend) && prevYearSpend !== 0;

  const growthRate = (
    ((budgetValue - prevYearSpend) / prevYearSpend) *
    100
  ).toFixed(2);

  const forecastLabel = forecastBefore
    ? `${prevYearBudgetText}Year Spend`
    : `${prevYearBudgetText}Year Forecast`;

  const forecastColor = forecastBefore
    ? theme.color.support.orange
    : theme.color.support.yellow;

  const showAdditionalInfo = showPreviousYear && forecast > 0;

  return (
    <>
      <When condition={!showPreviousYear}>
        <FlexContainer>
          <Text nowrap color={theme.color.text.text04} size="sm">
            {month} {year}
          </Text>
        </FlexContainer>
      </When>
      <FlexContainer columnGap={theme.spacing.spacing01}>
        <When condition={showPreviousYear}>
          <ColorBox color={theme.color.support.grey} />
        </When>
        <Text nowrap color={theme.color.text.text04} size="sm">
          {budgetText}Budget: {formatMoney(budget, currency)}
        </Text>
      </FlexContainer>
      <When condition={showPreviousYear}>
        <When condition={prevYearSpend !== 0}>
          <FlexContainer columnGap={theme.spacing.spacing01}>
            <ColorBox color={theme.color.support.yellow} />
            <Text nowrap color={theme.color.text.text04} size="sm">
              {abbreviateMonth(month)} {previousYear} Spend:{" "}
              {formatMoney(prevYearSpend, currency)}
            </Text>
          </FlexContainer>
        </When>
        <When condition={showAdditionalInfo}>
          <FlexContainer columnGap={theme.spacing.spacing01}>
            <ColorBox color={forecastColor} opacity={0.63} />
            <Text nowrap color={theme.color.text.text04} size="sm">
              {forecastLabel}: {formatMoney(forecast, currency)}
            </Text>
          </FlexContainer>
        </When>
        <When condition={showGrowthRate}>
          <FlexContainer columnGap={theme.spacing.spacing01}>
            <ColorBox color="transparent" />
            <Text nowrap color={theme.color.text.text04} size="sm">
              Growth Rate: {growthRate}%
            </Text>
          </FlexContainer>
        </When>
      </When>
    </>
  );
}
